import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import QRCode from 'react-qr-code';
import api from '../services/api';
import { toast } from 'react-toastify';
import RegisterAsset from '../components/RegisterAsset';
import PetManager from '../components/PetManager';
import FamilyManager from '../components/FamilyManager';
import NfcOfflineActions from '../components/NfcOfflineActions';
import ThemePicker from '../components/ThemePicker';
import '../styles/Dashboard.css';

const UserDashboard = () => {
  const { user } = useAuth();
  const [medicalInfo, setMedicalInfo] = useState({
    bloodType: '',
    allergies: [],
    medications: [],
    conditions: [],
    emergencyContact: {
      name: '',
      phone: '',
      relationship: ''
    },
    insuranceInfo: {
      provider: '',
      policyNumber: ''
    },
    dateOfBirth: '',
    height: '',
    weight: '',
    notes: ''
  });
  const [publicProfile, setPublicProfile] = useState({
    enabled: false,
    showFullName: true,
    showBloodType: true,
    showAllergies: true,
    showMedications: false,
    showConditions: false,
    showEmergencyContact: true,
    customMessage: ''
  });
  const [myAssets, setMyAssets] = useState([]);
  const [accessHistory, setAccessHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('qr');
  const [editing, setEditing] = useState(false);
  const { isUserTabEnabled, getUserTabLabel } = useSiteSettings();

  useEffect(() => {
    loadProfile();
    loadAccessHistory();
    loadMyAssets();
  }, []);

  const loadProfile = async () => {
    try {
      const res = await api.get('/api/users/profile');
      if (res.data.user.medicalInfo) {
        setMedicalInfo(res.data.user.medicalInfo);
      }
      if (res.data.user.publicProfile) {
        setPublicProfile(res.data.user.publicProfile);
      }
    } catch (error) {
      toast.error('Failed to load profile');
    }
  };

  const loadAccessHistory = async () => {
    try {
      const res = await api.get('/api/users/access-history');
      setAccessHistory(res.data.accessLogs);
    } catch (error) {
      console.error('Failed to load access history');
    }
  };

  const loadMyAssets = async () => {
    try {
      const res = await api.get('/api/assets/my-assets');
      setMyAssets(res.data.assets);
    } catch (error) {
      console.error('Failed to load assets');
    }
  };

  const handleMedicalInfoChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setMedicalInfo({
        ...medicalInfo,
        [parent]: { ...medicalInfo[parent], [child]: value }
      });
    } else {
      setMedicalInfo({ ...medicalInfo, [field]: value });
    }
  };

  const handleArrayChange = (field, value) => {
    const array = value.split(',').map(item => item.trim()).filter(item => item);
    setMedicalInfo({ ...medicalInfo, [field]: array });
  };

  const saveMedicalInfo = async () => {
    try {
      await api.put('/api/users/medical-info', medicalInfo);
      toast.success('Medical information updated successfully');
      setEditing(false);
    } catch (error) {
      toast.error('Failed to update medical information');
    }
  };

  const savePublicProfile = async () => {
    try {
      await api.put('/api/users/public-profile', publicProfile);
      toast.success('Public profile settings updated successfully');
    } catch (error) {
      toast.error('Failed to update public profile settings');
    }
  };

  const handlePublicProfileChange = (field, value) => {
    setPublicProfile({ ...publicProfile, [field]: value });
  };

  const unregisterAsset = async (assetId) => {
    if (window.confirm('Are you sure you want to unregister this asset?')) {
      try {
        await api.post(`/api/assets/unregister/${assetId}`);
        toast.success('Asset unregistered successfully');
        loadMyAssets();
      } catch (error) {
        toast.error('Failed to unregister asset');
      }
    }
  };

  const downloadQRCode = () => {
    const svg = document.getElementById('qr-code-svg');
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');
      
      const downloadLink = document.createElement('a');
      downloadLink.download = 'medical-qr-code.png';
      downloadLink.href = pngFile;
      downloadLink.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Welcome, {user?.firstName} {user?.lastName}</h1>
        <p className="user-email">{user?.email}</p>
      </div>

      <div className="dashboard-tabs">
        {[
          { key: 'qr',      fallback: 'QR Code'             },
          { key: 'medical', fallback: 'Medical Information'  },
          { key: 'assets',  fallback: 'My Assets'           },
          { key: 'public',  fallback: 'Public Profile'      },
          { key: 'access',  fallback: 'Access History'      },
          { key: 'pets',    fallback: '🐾 My Pets'          },
          { key: 'family',  fallback: '👨‍👩‍👧‍👦 Family' },
        ]
          .filter(t => isUserTabEnabled(t.key))
          .map(t => (
            <button
              key={t.key}
              className={activeTab === t.key ? 'tab active' : 'tab'}
              onClick={() => setActiveTab(t.key)}
            >
              {getUserTabLabel(t.key, t.fallback)}
            </button>
          ))
        }
        <button
          className={activeTab === 'appearance' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('appearance')}
        >
          🎨 Appearance
        </button>
      </div>

      {activeTab === 'qr' && (
        <div className="tab-content">
          <div className="qr-section">
            <h2>Your Medical QR Code</h2>
            <p>Share this QR code with authorized medical providers</p>
            
            <div className="qr-code-display">
              <QRCode 
                id="qr-code-svg"
                value={`${window.location.origin}/profile/${user?.qrCodeId}`}
                size={256}
                level="H"
              />
            </div>

            <button onClick={downloadQRCode} className="btn btn-secondary">
              Download QR Code
            </button>

            <div className="qr-info">
              <p><strong>QR Code ID:</strong> {user?.qrCodeId}</p>
              <p><strong>Profile URL:</strong> {window.location.origin}/profile/{user?.qrCodeId}</p>
            </div>

            {user?.qrCodeId && (
              <NfcOfflineActions
                profileUrl={`${window.location.origin}/profile/${user.qrCodeId}`}
                offlineUrl={`/api/offline/user/${user.qrCodeId}`}
                profileName={`${user.firstName} ${user.lastName}`}
                type="user"
              />
            )}
          </div>
        </div>
      )}

      {activeTab === 'medical' && (
        <div className="tab-content">
          <div className="medical-section">
            <div className="section-header">
              <h2>Medical Information</h2>
              {!editing ? (
                <button onClick={() => setEditing(true)} className="btn btn-primary">
                  Edit Information
                </button>
              ) : (
                <div>
                  <button onClick={saveMedicalInfo} className="btn btn-success">
                    Save Changes
                  </button>
                  <button onClick={() => setEditing(false)} className="btn btn-secondary">
                    Cancel
                  </button>
                </div>
              )}
            </div>

            <div className="medical-form">
              <div className="form-group">
                <label>Blood Type</label>
                <select
                  value={medicalInfo.bloodType}
                  onChange={(e) => handleMedicalInfoChange('bloodType', e.target.value)}
                  disabled={!editing}
                >
                  <option value="">Select Blood Type</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                </select>
              </div>

              <div className="form-group">
                <label>Date of Birth</label>
                <input
                  type="date"
                  value={medicalInfo.dateOfBirth ? medicalInfo.dateOfBirth.split('T')[0] : ''}
                  onChange={(e) => handleMedicalInfoChange('dateOfBirth', e.target.value)}
                  disabled={!editing}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Height</label>
                  <input
                    type="text"
                    placeholder="e.g., 5'10&quot;"
                    value={medicalInfo.height}
                    onChange={(e) => handleMedicalInfoChange('height', e.target.value)}
                    disabled={!editing}
                  />
                </div>

                <div className="form-group">
                  <label>Weight</label>
                  <input
                    type="text"
                    placeholder="e.g., 170 lbs"
                    value={medicalInfo.weight}
                    onChange={(e) => handleMedicalInfoChange('weight', e.target.value)}
                    disabled={!editing}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Allergies (comma-separated)</label>
                <input
                  type="text"
                  placeholder="e.g., Penicillin, Peanuts"
                  value={medicalInfo.allergies?.join(', ')}
                  onChange={(e) => handleArrayChange('allergies', e.target.value)}
                  disabled={!editing}
                />
              </div>

              <div className="form-group">
                <label>Current Medications (comma-separated)</label>
                <input
                  type="text"
                  placeholder="e.g., Aspirin, Lisinopril"
                  value={medicalInfo.medications?.join(', ')}
                  onChange={(e) => handleArrayChange('medications', e.target.value)}
                  disabled={!editing}
                />
              </div>

              <div className="form-group">
                <label>Medical Conditions (comma-separated)</label>
                <input
                  type="text"
                  placeholder="e.g., Diabetes, Hypertension"
                  value={medicalInfo.conditions?.join(', ')}
                  onChange={(e) => handleArrayChange('conditions', e.target.value)}
                  disabled={!editing}
                />
              </div>

              <h3>Emergency Contact</h3>
              
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={medicalInfo.emergencyContact?.name || ''}
                  onChange={(e) => handleMedicalInfoChange('emergencyContact.name', e.target.value)}
                  disabled={!editing}
                />
              </div>

              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={medicalInfo.emergencyContact?.phone || ''}
                  onChange={(e) => handleMedicalInfoChange('emergencyContact.phone', e.target.value)}
                  disabled={!editing}
                />
              </div>

              <div className="form-group">
                <label>Relationship</label>
                <input
                  type="text"
                  value={medicalInfo.emergencyContact?.relationship || ''}
                  onChange={(e) => handleMedicalInfoChange('emergencyContact.relationship', e.target.value)}
                  disabled={!editing}
                />
              </div>

              <h3>Insurance Information</h3>
              
              <div className="form-group">
                <label>Insurance Provider</label>
                <input
                  type="text"
                  value={medicalInfo.insuranceInfo?.provider || ''}
                  onChange={(e) => handleMedicalInfoChange('insuranceInfo.provider', e.target.value)}
                  disabled={!editing}
                />
              </div>

              <div className="form-group">
                <label>Policy Number</label>
                <input
                  type="text"
                  value={medicalInfo.insuranceInfo?.policyNumber || ''}
                  onChange={(e) => handleMedicalInfoChange('insuranceInfo.policyNumber', e.target.value)}
                  disabled={!editing}
                />
              </div>

              <div className="form-group">
                <label>Additional Notes</label>
                <textarea
                  rows="4"
                  value={medicalInfo.notes || ''}
                  onChange={(e) => handleMedicalInfoChange('notes', e.target.value)}
                  disabled={!editing}
                  placeholder="Any additional medical information..."
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'access' && (
        <div className="tab-content">
          <div className="access-history-section">
            <h2>Access History</h2>
            <p>Track who has viewed your medical information</p>

            {accessHistory.length === 0 ? (
              <p className="no-data">No access history yet</p>
            ) : (
              <div className="access-table">
                <table>
                  <thead>
                    <tr>
                      <th>Date & Time</th>
                      <th>Organization</th>
                      <th>Agent</th>
                      <th>Access Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accessHistory.map((log) => (
                      <tr key={log._id}>
                        <td>{new Date(log.timestamp).toLocaleString()}</td>
                        <td>{log.providerId?.organizationName || 'N/A'}</td>
                        <td>{log.agentName}</td>
                        <td>
                          <span className={`access-badge ${log.accessType}`}>
                            {log.accessType}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'assets' && (
        <div className="tab-content">
          <div className="assets-section">
            <h2>My Registered Assets</h2>
            <p>Physical QR code devices linked to your account</p>

            <RegisterAsset onAssetRegistered={loadMyAssets} />

            <div style={{ marginTop: '2rem' }}>
              <h3>Registered Devices</h3>
              {myAssets.length === 0 ? (
                <p className="no-data">No assets registered yet. Register your first asset above!</p>
              ) : (
                <div className="assets-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Asset ID</th>
                        <th>Type</th>
                        <th>Registration Date</th>
                        <th>Public URL</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {myAssets.map((asset) => (
                        <tr key={asset._id}>
                          <td><code>{asset.assetId}</code></td>
                          <td>{asset.assetType}</td>
                          <td>{new Date(asset.registrationDate).toLocaleDateString()}</td>
                          <td>
                            <a 
                              href={`/asset/${asset.assetId}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                            >
                              View Public Profile
                            </a>
                          </td>
                          <td>
                            <button
                              onClick={() => unregisterAsset(asset.assetId)}
                              className="btn btn-sm btn-danger"
                            >
                              Unregister
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'public' && (
        <div className="tab-content">
          <div className="public-profile-settings">
            <h2>Public Profile Settings</h2>
            <p>Control what information is visible when someone scans your QR code asset</p>

            <div className="info-box" style={{ marginBottom: '2rem' }}>
              <h4>ℹ️ About Public Profiles</h4>
              <p>
                When you register a physical QR code asset (card, bracelet, etc.), anyone who scans it
                can view your public profile WITHOUT needing to login. This is designed for emergency
                situations where first responders need quick access to your medical information.
              </p>
              <p>
                You have complete control over what information is shared publicly. Only enable this
                if you want your QR code assets to provide public emergency access.
              </p>
            </div>

            <div className="public-profile-form">
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={publicProfile.enabled}
                    onChange={(e) => handlePublicProfileChange('enabled', e.target.checked)}
                  />
                  <span>Enable Public Profile</span>
                </label>
                <small>Allow anyone with your asset QR code to view selected information</small>
              </div>

              {publicProfile.enabled && (
                <>
                  <h3>What Information to Share</h3>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={publicProfile.showFullName}
                        onChange={(e) => handlePublicProfileChange('showFullName', e.target.checked)}
                      />
                      <span>Show Full Name</span>
                    </label>
                    <small>If disabled, only first name will be shown</small>
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={publicProfile.showBloodType}
                        onChange={(e) => handlePublicProfileChange('showBloodType', e.target.checked)}
                      />
                      <span>Show Blood Type</span>
                    </label>
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={publicProfile.showAllergies}
                        onChange={(e) => handlePublicProfileChange('showAllergies', e.target.checked)}
                      />
                      <span>Show Allergies</span>
                    </label>
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={publicProfile.showMedications}
                        onChange={(e) => handlePublicProfileChange('showMedications', e.target.checked)}
                      />
                      <span>Show Current Medications</span>
                    </label>
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={publicProfile.showConditions}
                        onChange={(e) => handlePublicProfileChange('showConditions', e.target.checked)}
                      />
                      <span>Show Medical Conditions</span>
                    </label>
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={publicProfile.showEmergencyContact}
                        onChange={(e) => handlePublicProfileChange('showEmergencyContact', e.target.checked)}
                      />
                      <span>Show Emergency Contact</span>
                    </label>
                  </div>

                  <div className="form-group">
                    <label>Custom Message (optional)</label>
                    <textarea
                      rows="3"
                      value={publicProfile.customMessage}
                      onChange={(e) => handlePublicProfileChange('customMessage', e.target.value)}
                      placeholder="e.g., 'Thank you for helping! Please contact my emergency contact.'"
                    />
                    <small>This message will be displayed at the top of your public profile</small>
                  </div>
                </>
              )}

              <button onClick={savePublicProfile} className="btn btn-success">
                Save Public Profile Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'pets' && (
        <div className="tab-content">
          <PetManager />
        </div>
      )}

      {activeTab === 'family' && (
        <div className="tab-content">
          <FamilyManager currentUserId={user?.id || user?._id} />
        </div>
      )}

      {activeTab === 'appearance' && (
        <div className="tab-content">
          <ThemePicker scope="dashboard" />
        </div>
      )}
    </div>
  );
};

export default UserDashboard;
